import Vue from 'vue'
import VueNativeSock from 'vue-native-websocket'
import { v1 as uuidv1 } from 'uuid'
import { socketUrl } from '@/../../shared/src/utils/constants.js'
import { encryptText } from '@/../../shared/src/utils/encryption.js'
import { getActionMutations } from '@/../../shared/src/utils/actionMutations.js'
import { storageKey } from '@/../../shared/src/store/plugins/localStorage'
import { getFromLocalStorage } from '@/../../shared/src/store/plugins/localStorage'
import { formatSocketMessageArray } from '@/../../shared/src/utils/awssocket.js'
import { EventBus } from '@/../../shared/src/utils/eventbus.js'
import { delayCall } from '@/../../shared/src/utils.js'

let connectionManager
let heartbeatFunction
let actionMutationObj
let appName

const heartbeatInterval = (1000 * 60 * 4)

function initManualConnections (url) {
  Vue.use(VueNativeSock, url, {
    'connectManually': true
  })
  return new Vue()
}

function selectConnection (store, actionScope, action) {
  if (actionScope.includes('broadcast')) {
    return Object.values(store.state.socket.connections)
  }
  if (actionScope.includes('breakout') && action.type.startsWith('breakoutrooms/')) {
    const targetConnectionSlug = action.type.split('/')[1]
    return [store.state.socket.connections[`${appName}-${targetConnectionSlug}`]]
  }
  return [store.state.socket.connections[`${appName}-${store.state.config.classroom.event.slug}`]]
}

function trySendMessage (store, conn, msg) {
  try {
    conn.sendObj(msg)
  } catch (err) {
    store.dispatch('logger/logError', {
      'type': 'errors/socketSendErrorUnknown',
      'payload': {'error': {'name': 'Socket Send Message Error', 'message': err.name}, 'msg': msg}
    })
  }
}

function handleClosedConnection (store, conn, action) {

  const connectAttempts = store.state.socket.connectionAttempts[conn.socketChannelName] || 0

  if (action) {
    store.commit('socket/gatherUnsentMessages', {'channel': conn.socketChannelName, 'action': action})
  }

  if (connectAttempts < 3) {
    return store.dispatch('socket/reestablishConnections')
  } else {
    store.dispatch('logger/logError', {
      'type': 'errors/socketSendErrorClosed',
      'payload': {
        'error': {'name': 'Socket Send Message Error', 'message': 'websocket closed'},
        'connection': conn.url
      }
    })
    return EventBus.$emit('socketClosedErrorEvent')
  }

}

function startHeartbeat (store) {
  window.clearTimeout(heartbeatFunction)
  heartbeatFunction = window.setInterval(() => {
    store.dispatch('socket/fireHearbeat')
  }, heartbeatInterval)
}

export function vueAwsSocketPlugin (store, state) {

  appName = store.state.config.app
  actionMutationObj = getActionMutations(appName)

  window.removeEventListener('online', networkOutageHandler)
  window.addEventListener('online', networkOutageHandler)

  EventBus.$off('socketRefreshConnections').$on('socketRefreshConnections', networkOutageHandler)

  // const senderId = getUserId(store)
  const classSlug = store.state.config.classroom.event.slug

  function networkOutageHandler () {
    store.dispatch('socket/reestablishConnections', {'reconnectCurrentClass': true})
  }

  store.registerModule('socket', socketModule)
  const sockUrl = fullSocketUrl(getUserId(store), classSlug)
  connectionManager = initManualConnections(sockUrl)

  return store.subscribeAction(action => {

    const breakoutActionIndex = (action.type.lastIndexOf('/', action.type.lastIndexOf('/') - 1) + 1)
    const actionType = action.type.slice(breakoutActionIndex)
    const actionConfig = actionMutationObj[actionType]

    if (actionConfig && actionConfig.scope) {

      const connections = selectConnection(store, actionConfig.scope, action)
      const msgs = formatSocketMessageArray(actionType, action.payload, store.state.config.user.id, actionConfig)

      connections.forEach((conn) => {
        if (!conn) {
          const targetChannelSlug = action && action.payload && action.payload.slug ? action.payload.slug : classSlug
          store.commit('socket/gatherUnsentMessages', {'channel': `${appName}-${targetChannelSlug}`, 'action': action})
        } else if (conn && (conn.readyState === WebSocket.CLOSED) && window.navigator.onLine) {
          handleClosedConnection(store, conn, action)
        } else {
          msgs.map((msg, i) => {
            return delayCall(() => {
              trySendMessage(store, conn, msg)
            }, (50 * i))
          })
        }
      })
    }
  })
}

export function fullSocketUrl (userId, classId, siteId, compId, appNameOverride = appName) {
  const requestId = uuidv1()
  const securityToken = encodeURIComponent(encryptText(requestId))
  return `${socketUrl}?channel=${appNameOverride}-${classId}&username=${userId}&securityToken=${securityToken}&requestId=${requestId}&siteId=${siteId}&companyId=${compId}`
}

export function getUserId (store) {
  const classIdOverride = store.state.config.classroom.event.breakout_parent || store.state.config.classroom.event.slug
  const guestUser = getFromLocalStorage(storageKey(`guest-user`, classIdOverride))
  return guestUser ? guestUser.id : store.state.config.user.id
}

function getSocketChannelName (url) {
  return url.split('?channel=').pop().split('&')[0]
}

function getSocketSlug (url) {
  const channelName = getSocketChannelName(url)
  return channelName.slice(channelName.indexOf('-') + 1)
}

export const socketModule = {

  namespaced: true,

  state: {
    connections: {},
    messageCache: {},
    unsentMessages: {},
    connectionAttempts: {}
  },

  getters: {
    connectionsArray (state, getters) {
      return Object.values(state.connections)
    }
  },

  mutations: {

    onOpen (state, event) {
      const socketChannelName = getSocketChannelName(event.currentTarget.url)
      event.currentTarget.socketChannelName = socketChannelName
      state.connections[socketChannelName] = event.currentTarget
    },

    setConnectionAttempts (state, payload) {
      state.connectionAttempts[payload.socketChannelName] = payload.connectCount
    },

    addToMessageCache (state, payload) {
      const msgArray = state.messageCache[payload.msg.meta.id] || []
      state.messageCache[payload.msg.meta.id] = [...msgArray, payload.msg]
    },

    removeFromMessageCache (state, payload) {
      delete state.messageCache[payload.msgId]
    },

    removeClosedConnection (state, payload) {
      delete state.connections[payload.connectionId]
    },

    gatherUnsentMessages (state, payload) {
      const alreadyUnsentMessages = state.unsentMessages[payload.channel] || []
      state.unsentMessages[payload.channel] = [...alreadyUnsentMessages, payload.action]
    },

    clearUnsentMessages (state, payload) {
      state.unsentMessages[payload.channel] = []
    }
  },

  actions: {

    onopen ({state, commit, dispatch}, payload) {
      commit('onOpen', payload)
      commit('setConnectionAttempts', {'connectCount': 0, 'socketChannelName': payload.currentTarget.socketChannelName})
      dispatch('fireHearbeat')
      dispatch('sendUnsent', payload)
    },

    onclose (context, event) {
      context.dispatch('logger/logInfo', {
        'type': 'socket/onClose',
        'payload': {'code': event.code, 'reason': event.reason, 'message': event.message, 'status': event.status}
      }, {'root': true})
      return handleClosedConnection(this, event.target)
    },

    onerror ({state, rootState, commit, dispatch}, event) {
      dispatch('logger/logError', {
        'type': 'errors/socketError',
        'payload': {'code': event.code, 'reason': event.reason, 'message': event.message, 'status': event.status}
      }, {'root': true})
    },

    // default handler called for all methods
    onmessage ({state, rootState, commit, dispatch}, message) {
      const msg = JSON.parse(message.data)
      const targetSlug = getSocketSlug(message.currentTarget.url)
      const thisRoom = targetSlug === rootState.config.classroom.event.slug
      const validReceiver = msg.sender !== rootState.config.user.id
      const breakoutRoom = !!(rootState.breakoutrooms && rootState.breakoutrooms[targetSlug])
      const actionConfig = actionMutationObj[msg.actionMutation]

      if (actionConfig && msg.meta && validReceiver && thisRoom) {
        commit('addToMessageCache', {'msg': msg})
        dispatch('processMessages', {'msg': msg})
      }

      if (actionConfig && msg.meta && validReceiver && breakoutRoom && actionConfig.scope.includes('breakout')) {
        msg.actionMutation = `breakoutrooms/${targetSlug}/${msg.actionMutation}`
        commit('addToMessageCache', {'msg': msg})
        dispatch('processMessages', {'msg': msg})
      }

    },

    processMessages ({state, commit, dispatch}, payload) {
      const received = state.messageCache[payload.msg.meta.id]
      if (received.length === payload.msg.meta.totalMsgs) {
        const sortedReceived = received.sort((a, b) => a.meta.msgNumber - b.meta.msgNumber)
        const payloadString = sortedReceived.map((msg) => msg.payload).join('')
        this.commit(payload.msg.actionMutation, JSON.parse(payloadString))
        commit('removeFromMessageCache', {'msgId': payload.msg.meta.id})
      }
    },

    addManualConnection ({state, commit, getters, dispatch, rootState}, payload) {

      const siteId = rootState.config.user.school_id || rootState.config.classroom.event.owner.school_id
      const compId = rootState.config.classroom.event.company.id
      const connectionId = `${appName}-${payload.classId}`
      const alreadyConnected = state.connections[connectionId]

      if (alreadyConnected) {
        // Socket connection already exists
        if (alreadyConnected.readyState === 1) {
          // connection is live
          return true
        }
        if (alreadyConnected.readyState > 1) {
          // connection is closed. Remove
          commit('removeClosedConnection', {'connectionId': connectionId})
        }
      }

      const connectCount = (state.connectionAttempts[connectionId] || 0)
      commit('setConnectionAttempts', {'connectCount': connectCount + 1, 'socketChannelName': connectionId})

      setTimeout(() => {
        const url = fullSocketUrl(payload.userId, payload.classId, siteId, compId)
        connectionManager.$connect(url, {
          'store': this,
          'format': 'json',
          passToStoreHandler (eventName, event, next) {
            if (eventName.startsWith('SOCKET_')) {
              const actionName = eventName.replace('SOCKET_', '').toLowerCase()
              return dispatch(actionName, event)
            }
          }
        })
        startHeartbeat(this)
      }, (connectCount * 5000))
    },

    removeManualConnection ({state, commit, dispatch}, payload) {
      const connectionId = `${appName}-${payload.classId}`
      const connection = state.connections[connectionId]
      commit('removeClosedConnection', {'connectionId': connectionId})
      if (connection && connection.close) {
        connection.close()
      }
    },

    reestablishConnections ({state, commit, dispatch, rootState}, payload) {

      const currConnections = Object.keys(state.connections)

      currConnections.forEach((connectionId) => {
        const classId = connectionId.replace(`${appName}-`, '')
        dispatch('addManualConnection', {'classId': classId, 'userId': rootState.config.user.id})
      })

      if (!currConnections.length && payload && payload.reconnectCurrentClass) {
        dispatch('addManualConnection', {'classId': rootState.config.classroom.event.slug, 'userId': rootState.config.user.id})
      }

    },

    // forceClose ({state, rootState}, payload) {
    //   const message = {'action': 'onMessage', 'messagePayload': {'sender': rootState.config.user.id, 'forceClose': true}}
    //   return Object.values(state.connections)
    //     .forEach((conn) => {
    //       if (conn && (conn.readyState === WebSocket.CLOSED) && window.navigator.onLine) {
    //         handleClosedConnection(this, conn)
    //       } else {
    //         trySendMessage(this, conn, message)
    //       }
    //   })
    // },

    fireHearbeat ({state, rootState}, payload) {
      const message = {'action': 'onMessage', 'messagePayload': {'sender': rootState.config.user.id, 'heartbeat': {}, 'meta': {'broadcast': false, 'store': false}}}
      return Object.values(state.connections)
        .forEach((conn) => {
          if (conn && (conn.readyState === WebSocket.CLOSED) && window.navigator.onLine) {
            handleClosedConnection(this, conn)
          } else {
            trySendMessage(this, conn, message)
          }
        })
    },

    sendUnsent ({state, getters, commit, dispatch, rootState}, payload) {

      const conn = payload.currentTarget || payload.target
      const unsentMessages = state.unsentMessages[conn.socketChannelName] || []

      unsentMessages.map((action, i) => {
        return delayCall(() => {
          dispatch(action.type, action.payload, {'root': true})
        }, (100 * i))
      })
      commit('clearUnsentMessages', {'channel': conn.socketChannelName})
    }

  }

}
